import { Loading } from 'components/uikit/suspense/Loading';
import { QueryFactory } from 'services/api';
import styles from './TemplateList.module.scss';
import React, { useEffect, useMemo, useState } from 'react';
import FolderIcon from 'assets/icons/folder.svg?react';
import DocumentIcon from 'assets/icons/document.svg?react';
import { TreeItems } from 'dnd-kit-sortable-tree';
import { ItemChangedReason } from 'dnd-kit-sortable-tree/dist/types';
import { useModal } from 'components/uikit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { FabButton } from 'components/uikit/buttons/FabButton';
import clsx from 'clsx';
import { AppMenu, AppMenuItem } from 'components/uikit/menu/AppMenu';
import { MenuDirection } from 'components/uikit/menu/MenuDirection';
import { TemplateListItem } from './TemplateListItem';
import { useDocumentTree } from 'helpers/documents/useDocumentTree';
import { useTreeSearch } from 'helpers/documents/useTreeSearch';
import { useTreeActions } from 'helpers/documents/useTreeActions';
import {
  BaseTemplateTreeNode,
  isTemplate,
  TemplateFolderNode,
  TemplateTreeNode,
} from 'helpers/documents/types/templates';
import {
  checkIsExpandDisabled,
  expandAll,
  isItemExported,
  isSameDepth,
  setItemUnexported,
  updateTemplateTreeProperties,
} from 'helpers/documents/documentsObserver';
import {
  BaseNode,
  NodeType,
  parseProjectElements,
} from 'helpers/documents/types/base';
import { createTemplates, selectFiles } from 'helpers/documents/fileHandler';
import { AddFolderModal } from 'pages/authorized/documents/documentList/addFolder/AddFolderModal';
import { TreeControlPanel } from 'components/documents/TreeControlPanel';
import { FolderList } from 'components/documents/FolderList';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import DocumentsPlaceholder from 'assets/icons/documents-placeholder.svg?react';
import { useGridTemplate } from 'helpers/useGridTemplate';

export const TemplateList: React.FC = () => {
  const foldersQuery = QueryFactory.TemplateQuery.useGetFoldersQuery();
  const templateQuery = QueryFactory.TemplateQuery.useSearchQuery();
  const queryClient = useQueryClient();
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const linkRef = React.useRef<HTMLElement>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const i18n = useScopedTranslation('Page.Templates.TemplateList');
  const modalTranslation = useScopedTranslation('Component.CommonModals');
  const i18nMenu = useScopedTranslation('Page.Templates.menuItems');

  const {
    items,
    setItems,
    itemsRef,
    onChangeItems,
    onChangeItemsWithInvalidation,
  } = useDocumentTree<BaseTemplateTreeNode>({
    onPatch: async (cleanItems) => {
      await QueryFactory.TemplateClient.patchFolders({ folders: cleanItems });
    },
    onInvalidate: async () => {
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.getFoldersQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.searchQueryKey(),
      });
    },
  });

  const { searchingName, setSearchingName, filteredItems } =
    useTreeSearch(items);

  const { isLoading, setIsLoading, onItemRemove, onItemEdit, onAddSubItem } =
    useTreeActions({
      itemsRef,
      onChangeItemsWithInvalidation,
      onNodeDelete: async (node) => {
        if (isTemplate(node))
          await QueryFactory.TemplateClient.delete_(node.documentId, true);
      },
    });

  const confirmMoveModal = useModal();
  const isExpandDisabled = checkIsExpandDisabled(items);

  const confirmBeforeMove = async (
    changedItems: TreeItems<BaseNode>,
    reason: ItemChangedReason<BaseNode>,
  ) => {
    const draggedItem = reason.type === 'dropped' ? reason.draggedItem : null;
    const fromParent =
      reason.type === 'dropped' ? reason.draggedFromParent : null;
    const toParent = reason.type === 'dropped' ? reason.droppedToParent : null;

    const shouldConfirm =
      draggedItem &&
      isItemExported(draggedItem!) &&
      !isSameDepth(fromParent, toParent);

    if (shouldConfirm) {
      setIsLoading(true);
      const confirmed = await confirmMoveModal.showConfirm({
        title: modalTranslation.t('confirm_change'),
        text: modalTranslation.t('confirm_change_message'),
      });
      if (confirmed) {
        await setItemUnexported(draggedItem!);
        await onChangeItemsWithInvalidation(changedItems);
      }
    } else {
      await onChangeItems(changedItems);
    }
    setIsLoading(false);
  };

  const invalidateWithDependencies = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.getFoldersQueryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.searchQueryKey(),
      }),
    ]);
  };

  const onShowContextualMenu = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.stopPropagation();
      ev.preventDefault();
      setShowContextualMenu(true);
    },
    [],
  );

  const menuItems: AppMenuItem[] = useMemo(() => {
    const uploadTemplateItem: AppMenuItem = {
      key: 'upload_template',
      text: i18nMenu.t('upload_template'),
      icon: <DocumentIcon className={styles.contextIcon} />,
      className: styles.menuItem,
      onClick: async () => {
        const files = await selectFiles(true, true);
        setIsLoading(true);
        setShowContextualMenu(false);
        try {
          await createTemplates(files, invalidateWithDependencies);
        } finally {
          setIsLoading(false);
        }
      },
    };

    const newFolderItem: AppMenuItem = {
      key: 'new_folder',
      text: i18n.t('create_folder'),
      icon: <FolderIcon className={styles.contextIcon} />,
      className: styles.menuItem,
      onClick: () => setIsModalOpen(true),
    };

    return [uploadTemplateItem, newFolderItem];
  }, []);

  const onHideContextualMenu = React.useCallback(
    () => setShowContextualMenu(false),
    [],
  );

  const getCopiedItems = (items: TreeItems<BaseNode>): TreeItems<BaseNode> => {
    const serializedItems = JSON.stringify(items);
    return JSON.parse(serializedItems);
  };

  const updateProps = {
    onDelete: onItemRemove,
    onItemEdit: onItemEdit,
    onAddSubItem: onAddSubItem,
  };

  useEffect(() => {
    if (!templateQuery.data?.data) return;
    if (!foldersQuery?.data) return;

    const fetchedItems = parseProjectElements(
      foldersQuery.data,
      NodeType.Template,
    );
    if (fetchedItems.length > 0) {
      setItems(
        updateTemplateTreeProperties(
          fetchedItems,
          templateQuery.data.data,
          updateProps,
        ),
      );
    } else {
      setItems(fetchedItems);
    }
  }, [foldersQuery.data, templateQuery.data?.data]);

  const Footer = () => {
    return (
      <div>
        <div className={styles.footer} />
        <AddFolderModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            onHideContextualMenu();
          }}
          createFolderNode={async (name, id) => {
            const items_copy = getCopiedItems(items);
            items_copy.push({
              id,
              type: NodeType.Folder,
              name,
              children: [],
              collapsed: true,
              onDelete: async (folderId) => await onItemRemove(folderId),
              onFolderEdit: async (folderId, editedName, nameKey) =>
                await onItemEdit(folderId, editedName, nameKey),
              onAddSubItem: async (folderId, node) => {
                await onAddSubItem(folderId, node);
              },
            } as TemplateFolderNode);

            await onChangeItemsWithInvalidation([...items_copy]);
          }}
        />
      </div>
    );
  };

  const { hiddenColumns, hideableColumns, grid } = useGridTemplate(
    'template', // table id
    'TemplateList', // grid type
  );

  const Header = () => {
    return (
      <div
        className={items.length > 0 ? styles.header : styles.emptyTreeHeader}
        style={{ gridTemplateColumns: grid }}
      >
        <div className={styles.headerName}>{i18n.t('name')}</div>
        <div
          className={clsx(styles.headerActivity, styles.column, {
            [styles.columnHidden]: hiddenColumns.includes('activity'),
          })}
        >
          {i18n.t('activity')}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.body} data-test-id="template-list">
      <Loading
        className={styles.loader}
        loading={isLoading || foldersQuery.isLoading || templateQuery.isLoading}
      >
        <div className={styles.title}>{i18n.t('title')}</div>
        <div className={styles.mainContainer}>
          <TreeControlPanel
            isExpandDisabled={isExpandDisabled}
            searchValue={searchingName}
            onSearchChange={setSearchingName}
            styles={{
              controlContainer: styles.controlContainer,
              actionsContainer: styles.searchContainer,
            }}
            tableColumns={hideableColumns.map((value) => ({
              value,
              label: i18n.t(value),
            }))}
            onExpand={async (isExpanded) => {
              expandAll(itemsRef.current, isExpanded);
              await onChangeItems(itemsRef.current);
            }}
          />
          <div className={styles.treeContainer}>
            <FolderList<TemplateTreeNode>
              items={filteredItems as TemplateTreeNode[]}
              onChangeItems={
                searchingName !== ''
                  ? async () => {}
                  : async (items, reason) =>
                      await confirmBeforeMove(items, reason)
              }
              footer={<Footer />}
              header={<Header />}
              listItem={TemplateListItem}
              styles={{
                tree: styles.tree,
                scrollWrapper: styles.scrollWrapper,
              }}
              noItemsPlaceholder={
                <div className={styles.placeHolderContainer}>
                  <DocumentsPlaceholder className={styles.placeHolder} />
                </div>
              }
            />
            <div className={styles.fab}>
              <a
                onClick={(e) => {
                  onShowContextualMenu(e);
                }}
                href="#"
                ref={linkRef as any}
              >
                <FabButton
                  testId="add-item-button"
                  className={clsx(showContextualMenu && styles.fabButton)}
                />
              </a>
              <AppMenu
                menuItems={menuItems}
                open={showContextualMenu}
                anchorEl={linkRef.current}
                className={styles.appMenu}
                onClose={onHideContextualMenu}
                direction={MenuDirection.topCenter}
              />
            </div>
          </div>
        </div>
      </Loading>
    </div>
  );
};
